<template>
  <div class="d-flex align-center">
    <div class="mr-4">
      <v-progress-circular v-if="recargando" indeterminate color="primary" />

      <div v-else>
        <v-btn v-if="!autoreiniciar && tiempoRestante === 0" color="primary" @click="recargaManual()">
          Recargar <v-icon class="ml-2" right>mdi-reload</v-icon>
        </v-btn>

        <v-checkbox
          v-else-if="permitirDesactivarConsulta"
          v-model="modelValue"
          :label="`Próxima actualización en: ${modelValue ? formatearTiempoRestante(tiempoRestante) : 'Desactivado'}`"
        />

        <span v-else class="h5">
          Próxima actualización en: {{ formatearTiempoRestante(tiempoRestante) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
const nowMs = () => Date.now();

function detenerTimer(vm) {
  if (vm._timer) {
    clearInterval(vm._timer);
    vm._timer = null;
  }
}

function iniciarTimer(vm) {
  detenerTimer(vm);
  vm._disparoFin = false;
  vm.inicioMs = nowMs();
  vm.ahoraMs = vm.inicioMs;

  vm._timer = setInterval(() => {
    vm.ahoraMs = nowMs();
  }, 1000);
}

function reiniciarCuenta(vm) {
  vm.segundosGuardados = vm.recordarProgreso ? vm.duracionSegundos : null;
  vm.inicioMs = nowMs();
  vm.ahoraMs = vm.inicioMs;
  vm._disparoFin = false;
}

function asegurarBaseNoCero(vm) {
  if (!vm.recordarProgreso) {
    vm.segundosGuardados = null;
    return;
  }
  if (vm.segundosGuardados == null || vm.segundosGuardados <= 0) {
    vm.segundosGuardados = vm.duracionSegundos;
  }
}

function calcularRestanteReal(vm) {
  if (!vm.consultaAutomaticaActiva) return 0;

  const base =
    vm.recordarProgreso && vm.segundosGuardados != null
      ? vm.segundosGuardados
      : vm.duracionSegundos;

  const transcurridos = Math.floor((nowMs() - vm.inicioMs) / 1000);
  return Math.max(0, base - transcurridos);
}

// ✅ Pausa REAL (aquí sí se puede recordar progreso)
function pausarPorPausa(vm) {
  if (vm.recordarProgreso) {
    vm.segundosGuardados = calcularRestanteReal(vm);
  } else {
    vm.segundosGuardados = null; // al reanudar, reinicia
  }

  detenerTimer(vm);
  vm.inicioMs = nowMs();
  vm.ahoraMs = vm.inicioMs;
  vm._disparoFin = false;
}

// ✅ Pausa por RECARGA (NO guarda progreso)
function pausarPorRecargando(vm) {
  detenerTimer(vm);
  vm.inicioMs = nowMs();
  vm.ahoraMs = vm.inicioMs;
}

function recargaManual() {
  this.$emit("recargaManual");
  reiniciarCuenta(this);

  if (this.consultaAutomaticaActiva && !this.recargando && !this.pausado) {
    iniciarTimer(this);
  }
}

export default {
  props: {
    value: { type: Boolean, default: true },
    duracionSegundos: { type: Number, default: 60 },
    recargando: { type: Boolean, default: false },
    autoreiniciar: { type: Boolean, default: true },
    recordarProgreso: { type: Boolean, default: true },
    permitirDesactivarConsulta: { type: Boolean, default: true },

    // ✅ NUEVA PROP
    pausado: { type: Boolean, default: false },
  },
  data() {
    const t = nowMs();
    return {
      ahoraMs: t,
      inicioMs: t,
      _timer: null,
      _disparoFin: false,
      segundosGuardados: null,
    };
  },
  computed: {
    modelValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    consultaAutomaticaActiva() {
      return this.permitirDesactivarConsulta ? this.modelValue : true;
    },
    tiempoRestante() {
      if (!this.consultaAutomaticaActiva) return 0;

      const base =
        this.recordarProgreso && this.segundosGuardados != null
          ? this.segundosGuardados
          : this.duracionSegundos;

      const transcurridos = Math.floor((this.ahoraMs - this.inicioMs) / 1000);
      return Math.max(0, base - transcurridos);
    },
  },
  watch: {
    recargando(nuevo) {
      // Si está pausado, no tocamos nada (para no perder el progreso guardado por pausa)
      if (this.pausado) return;

      if (nuevo) {
        pausarPorRecargando(this);
        return;
      }

      // Al terminar la recarga, SIEMPRE reinicia el conteo (no “recuerda”)
      if (this.consultaAutomaticaActiva) {
        reiniciarCuenta(this);
        iniciarTimer(this);
      }
    },

    pausado(nuevo) {
      if (nuevo) {
        pausarPorPausa(this);
        return;
      }

      // Reanudar
      if (this.consultaAutomaticaActiva && !this.recargando) {
        asegurarBaseNoCero(this);
        iniciarTimer(this);
      }
    },

    modelValue(nuevo) {
      if (!this.permitirDesactivarConsulta) {
        if (!nuevo) this.$emit("input", true);

        if (!this.recargando && !this.pausado) {
          asegurarBaseNoCero(this);
          iniciarTimer(this);
        }
        return;
      }

      if (!nuevo) {
        // Apagar: conservamos comportamiento anterior (guarda restante si recordarProgreso)
        if (this.recordarProgreso) this.segundosGuardados = calcularRestanteReal(this);
        else this.segundosGuardados = null;

        detenerTimer(this);
        return;
      }

      if (!this.recargando && !this.pausado) {
        asegurarBaseNoCero(this);
        iniciarTimer(this);
      }
    },

    permitirDesactivarConsulta(nuevo) {
      if (nuevo) return;

      if (!this.modelValue) this.$emit("input", true);
      if (!this.recargando && !this.pausado) {
        asegurarBaseNoCero(this);
        iniciarTimer(this);
      }
    },

    recordarProgreso(nuevo) {
      if (nuevo) {
        this.segundosGuardados = calcularRestanteReal(this);
        asegurarBaseNoCero(this);
      } else {
        this.segundosGuardados = null;
      }
    },

    duracionSegundos() {
      reiniciarCuenta(this);
      if (this.consultaAutomaticaActiva && !this.recargando && !this.pausado) {
        iniciarTimer(this);
      }
    },

    tiempoRestante(nuevo) {
      if (nuevo !== 0) {
        this._disparoFin = false;
        return;
      }

      if (!this.autoreiniciar) {
        detenerTimer(this);
        return;
      }

      if (!this.consultaAutomaticaActiva || this.recargando || this.pausado || this._disparoFin) return;

      this._disparoFin = true;
      this.$emit("recargaManual");

      reiniciarCuenta(this);
      iniciarTimer(this);
    },
  },
  mounted() {
    this.segundosGuardados = this.recordarProgreso ? this.duracionSegundos : null;

    if (!this.permitirDesactivarConsulta && !this.modelValue) this.$emit("input", true);

    if (this.consultaAutomaticaActiva && !this.recargando && !this.pausado) {
      iniciarTimer(this);
    }
  },
  beforeDestroy() {
    detenerTimer(this);
  },
  methods: {
    recargaManual,
    formatearTiempoRestante(segundos) {
      const total = Math.max(0, Math.floor(Number(segundos) || 0));

      const horas = Math.floor(total / 3600);
      const minutos = Math.floor((total % 3600) / 60);
      const segs = total % 60;

      const hh = String(horas).padStart(2, "0");
      const mm = String(minutos).padStart(2, "0");
      const ss = String(segs).padStart(2, "0");

      return `${hh}:${mm}:${ss}`;
    },
  },
};
</script>
